import React from 'react';
import { Edit2, Trash2, Network } from 'lucide-react';

const TIPO_LABELS = {
  division: 'División',
  sucursal: 'Sucursal',
  sede:     'Sede',
  region:   'Región',
  area:     'Área',
  holding:  'Holding',
  unidad:   'Unidad',
};

const TIPO_COLORS = {
  division: 'bg-blue-100 text-blue-700',
  sucursal: 'bg-emerald-100 text-emerald-700',
  sede:     'bg-violet-100 text-violet-700',
  region:   'bg-amber-100 text-amber-700',
  area:     'bg-cyan-100 text-cyan-700',
  holding:  'bg-rose-100 text-rose-700',
  unidad:   'bg-gray-100 text-gray-700',
};

export default function OrgUnitList({ orgUnits, onEdit, onDelete }) {
  if (orgUnits.length === 0) {
    return (
      <div className="py-16 text-center">
        <Network className="w-12 h-12 text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500 mb-1">No hay unidades organizativas registradas</p>
        <p className="text-xs text-gray-400">Crea la primera unidad para comenzar</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Unidad</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Tipo</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Pertenece a</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Responsable</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Depts.</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {orgUnits.map(unit => (
            <tr key={unit.id} className="hover:bg-gray-50 transition-colors">

              {/* Nombre */}
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-10 rounded-full flex-shrink-0"
                    style={{ backgroundColor: unit.color }}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{unit.nombre}</div>
                    {unit.descripcion && (
                      <div className="text-xs text-gray-500 truncate max-w-xs mt-0.5">
                        {unit.descripcion}
                      </div>
                    )}
                  </div>
                </div>
              </td>

              {/* Tipo */}
              <td className="px-6 py-4">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${TIPO_COLORS[unit.tipo] || TIPO_COLORS.unidad}`}>
                  {TIPO_LABELS[unit.tipo] || unit.tipo}
                </span>
              </td>

              {/* Pertenece a */}
              <td className="px-6 py-4">
                <span className="text-sm text-gray-700">
                  {unit.parent_nombre || <span className="text-gray-400 italic">Raíz</span>}
                </span>
              </td>

              {/* Responsable */}
              <td className="px-6 py-4">
                <span className="text-sm text-gray-700">{unit.responsable || '—'}</span>
              </td>

              {/* Dept count */}
              <td className="px-6 py-4 text-center">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                  {unit.dept_count ?? 0}
                </span>
              </td>

              {/* Acciones */}
              <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => onEdit(unit)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      const deptCount = unit.dept_count ?? 0;
                      if (deptCount > 0) {
                        alert(`No puede eliminar "${unit.nombre}" porque tiene ${deptCount} departamento(s) asignado(s).`);
                        return;
                      }
                      if (window.confirm(`¿Eliminar la unidad "${unit.nombre}"?\n\nEsta acción no se puede deshacer.`)) {
                        onDelete(unit.id);
                      }
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Eliminar
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 border-t border-gray-200">
          <tr>
            <td colSpan="6" className="px-6 py-3">
              <p className="text-xs text-gray-500">
                <span className="font-semibold text-gray-700">{orgUnits.length}</span>{' '}
                unidad{orgUnits.length !== 1 ? 'es' : ''} organizativa{orgUnits.length !== 1 ? 's' : ''}
              </p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
